class QuickViewDrawer extends MenuDrawer {
  constructor() {
    super();

    this.setClasses({
      open: 'quick-view--open',
      opening: 'quick-view--opening',
      closing: 'quick-view--closing'
    });

    this.addEventListener('close', () => {
      const drawerContent = this.querySelector('.quick-view__content');
      drawerContent.innerHTML = '';
      drawerContent.classList.remove('hide-cover');

      document.dispatchEvent(new CustomEvent('quickview:close'));
    });
  }
}
customElements.define('quick-view-drawer', QuickViewDrawer);

class QuickViewButton extends MenuDrawer {
  constructor() {
    super();

    this.addEventListener('click', () => {
      const wrapper = this.closest('.card-wrapper');
      const drawer = wrapper.querySelector('quick-view-drawer');
      if (drawer) {
        drawer.querySelector('summary').click();

        document.dispatchEvent(new CustomEvent('quickview:open', {
          detail: {
            productUrl: this.dataset.productUrl
          }
        }));
      }
      else if (this.dataset.productUrl) {
        window.location.href = this.dataset.productUrl;
      }
    });
  }
}
customElements.define('quick-view-button', QuickViewButton);

class QuickView extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);
  }

  handleIntersection(entries, _observer) {
    if (!entries[0].isIntersecting) return;

    const selector = '.quick-view__content';
    const drawerContent = this.querySelector(selector);
    const productUrl = this.dataset.productUrl.split('?')[0];
    const sectionUrl = `${productUrl}?view=modal`;

    fetch(sectionUrl)
      .then(response => response.text())
      .then(responseText => {
        setTimeout(() => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          const productElement = responseHTML.querySelector(selector);
          this.setInnerHTML(drawerContent, productElement.innerHTML);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          // Check if we're in bundle mode and modify the form
          this.setupBundleMode(drawerContent);
        }, 200);

        setTimeout(() => {
          drawerContent.classList.add('hide-cover');

          document.dispatchEvent(new CustomEvent('quickview:loaded', {
            detail: {
              productUrl: this.dataset.productUrl
            }
          }));
        }, 500);
      })
      .catch(e => {
        console.error(e);
      });
  }

  setupBundleMode(content) {
    // Check if there's a bundle-builder element on the page
    const bundleBuilder = document.querySelector('bundle-builder');
    if (!bundleBuilder) return;
    
    // Find the product form in the quick view
    const productForm = content.querySelector('product-form, form[action*="/cart/add"]');
    if (!productForm) return;
    
    // Get product data from the quick view content
    const productJson = content.querySelector('[data-product-json]');
    let product = null;
    
    if (productJson) {
      try {
        product = JSON.parse(productJson.textContent);
      } catch (e) {
        console.error('Quick View: Failed to parse product JSON', e);
      }
    }
    
    // Get product data from the card wrapper as fallback
    const cardWrapper = this.closest('.card-wrapper');
    const productId = cardWrapper?.dataset.productId || product?.id;
    const variantId = cardWrapper?.dataset.variantId || product?.variants?.[0]?.id;
    const productTitle = cardWrapper?.dataset.productTitle || product?.title;
    const productPrice = cardWrapper?.dataset.productPrice || (product?.price);
    const productImage = cardWrapper?.dataset.productImage || product?.featured_image;
    
    // Find the add to cart button
    const addToCartBtn = content.querySelector('[name="add"], .product-form__submit, button[type="submit"]');
    if (!addToCartBtn) return;
    
    // Create bundle add button
    const bundleAddBtn = document.createElement('button');
    bundleAddBtn.type = 'button';
    bundleAddBtn.className = 'bundle-add-btn quick-view__bundle-btn button button--full-width';
    bundleAddBtn.setAttribute('data-bundle-add', '');
    bundleAddBtn.textContent = 'Ajouter au bundle';
    
    // Create quantity selector for bundle (hidden initially)
    const bundleQtySelector = document.createElement('div');
    bundleQtySelector.className = 'bundle-quantity quick-view__bundle-quantity';
    bundleQtySelector.setAttribute('data-bundle-quantity', '');
    bundleQtySelector.style.display = 'none';
    bundleQtySelector.innerHTML = `
      <button type="button" class="bundle-qty-btn" data-bundle-qty-minus>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
      </button>
      <input type="number" class="bundle-qty-input" value="1" min="0" max="99" data-bundle-qty-input>
      <button type="button" class="bundle-qty-btn" data-bundle-qty-plus>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
      </button>
    `;
    
    // Create a container for bundle controls
    const bundleContainer = document.createElement('div');
    bundleContainer.className = 'quick-view__bundle-actions';
    bundleContainer.appendChild(bundleAddBtn);
    bundleContainer.appendChild(bundleQtySelector);
    
    // Hide original add to cart and insert bundle button
    addToCartBtn.style.display = 'none';
    addToCartBtn.parentNode.insertBefore(bundleContainer, addToCartBtn.nextSibling);
    
    // Also hide payment buttons if present
    const paymentBtns = content.querySelector('.shopify-payment-button');
    if (paymentBtns) paymentBtns.style.display = 'none';
    
    // Set up event listeners
    let quantity = 1;
    let isAdded = false;
    
    const getVariantId = () => {
      // Try to get selected variant from variant picker
      const variantInput = content.querySelector('input[name="id"]');
      if (variantInput) return variantInput.value;
      
      // Fallback to default variant
      return variantId;
    };
    
    const getPrice = () => {
      // Try to get current price from price element
      const priceEl = content.querySelector('.price__regular .price-item--regular, .price-item--sale');
      if (priceEl) {
        const priceText = priceEl.textContent.replace(/[^\d.,]/g, '').replace(',', '.');
        return Math.round(parseFloat(priceText) * 100);
      }
      return parseInt(productPrice) || 0;
    };
    
    const getImage = () => {
      const imgEl = content.querySelector('.product__media img, .product-single__media img');
      if (imgEl) return imgEl.src;
      return productImage;
    };
    
    bundleAddBtn.addEventListener('click', () => {
      bundleAddBtn.style.display = 'none';
      bundleQtySelector.style.display = 'flex';
      quantity = 1;
      isAdded = true;
      
      // Dispatch event to bundle builder
      document.dispatchEvent(new CustomEvent('bundle:addFromQuickView', {
        detail: {
          productId: productId,
          variantId: getVariantId(),
          title: productTitle,
          price: getPrice(),
          image: getImage(),
          quantity: 1
        }
      }));
    });
    
    const qtyMinus = bundleQtySelector.querySelector('[data-bundle-qty-minus]');
    const qtyPlus = bundleQtySelector.querySelector('[data-bundle-qty-plus]');
    const qtyInput = bundleQtySelector.querySelector('[data-bundle-qty-input]');
    
    const updateQuantity = (newQty) => {
      if (newQty < 0) newQty = 0;
      if (newQty > 99) newQty = 99;
      
      if (newQty === 0) {
        // Reset to add button
        bundleAddBtn.style.display = '';
        bundleQtySelector.style.display = 'none';
        isAdded = false;
      }
      
      quantity = newQty;
      qtyInput.value = quantity;
      
      // Dispatch event to bundle builder
      document.dispatchEvent(new CustomEvent('bundle:addFromQuickView', {
        detail: {
          productId: productId,
          variantId: getVariantId(),
          title: productTitle,
          price: getPrice(),
          image: getImage(),
          quantity: quantity
        }
      }));
    };
    
    qtyMinus.addEventListener('click', () => updateQuantity(quantity - 1));
    qtyPlus.addEventListener('click', () => updateQuantity(quantity + 1));
    qtyInput.addEventListener('change', () => updateQuantity(parseInt(qtyInput.value) || 0));
  }

  setInnerHTML(element, html) {
    element.innerHTML = html;

    // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    element.querySelectorAll('script').forEach(oldScriptTag => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach(attribute => {
        newScriptTag.setAttribute(attribute.name, attribute.value)
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}
customElements.define('quick-view', QuickView);
